package ui

import com.soywiz.korge.input.*
import com.soywiz.korge.ui.*
import com.soywiz.korge.view.*
import com.soywiz.korim.bitmap.*
import com.soywiz.korim.text.*
import com.soywiz.korio.async.*
import com.soywiz.korma.geom.*
import input.*

data class ControllerSignal(
    val buttonId: Int,
    var pressing: Boolean
)

class CustomButton(
    label: String,
    width: Double,
    height: Double,
    bgIcon: BmpSlice? = null,
    icon: BmpSlice? = null,
    activeIcon: BmpSlice? = null,
    disabledIcon: BmpSlice? = null,
    val buttonId: Int
) {
    private val thisButton = this

    val text = UIText(label, width, height).apply {
        textAlignment = TextAlignment.MIDDLE_CENTER
    }

    private val bgImage = bgIcon?.let {
        Image(bitmap = bgIcon).apply {
            fitImageInRect(this, bgIcon, width, height, Anchor.CENTER)
        }
    }

    private val iconImage = icon?.let {
        Image(bitmap = icon).apply {
            fitImageInRect(this, icon, width, height, Anchor.CENTER)
        }
    }

    private val activeImageIcon = activeIcon?.let {
        Image(bitmap = activeIcon).apply {
            fitImageInRect(this, activeIcon, width, height, Anchor.CENTER)
        }
    }

    private val disabledImageIcon = disabledIcon?.let {
        Image(bitmap = disabledIcon).apply {
            fitImageInRect(this, disabledIcon, width, height, Anchor.CENTER)
        }
    }

    var forcedPressed: Boolean = false

    var enabled: Boolean = true
        private set

    var pressing = false
        private set
    var eventId: Int? = null
        private set
    var clicked = false
        private set

    val controllerSignal = Signal<ControllerSignal>()
    private val controllerSignalData = ControllerSignal(buttonId, pressing)

    val onPress = Signal<Unit>()
    val onUp = Signal<Unit>()
    val onUpAnywhere = Signal<Unit>()

    val container = Container().apply {
        bgImage?.addTo(this)
        iconImage?.addTo(this)
        activeImageIcon?.addTo(this)
        disabledImageIcon?.addTo(this)
        text.addTo(this)

        activeImageIcon?.visible = false
        disabledImageIcon?.visible = false

        val view = this

        touch {
            start {
                if (enabled) {
                    if (view.hitTest(it.global) != null &&
                        !pressing &&
                        eventId == null &&
                        !clicked
                    ) {
                        pressing = true
                        eventId = it.id
                        setActive()
                        onPress.invoke()
                        handleController()
                    }
                }
            }

            end {
                if (it.id == eventId && !clicked) {
                    eventId = null
                    pressing = false
                    setIdle()
                    if (view.hitTest(it.global) != null) {
                        onUp.invoke()
                    }
                    onUpAnywhere()
                    handleController()
                }
            }
        }

        onDown {
            if (enabled) {
                if (!pressing) {
                    pressing = true
                    clicked = true
                    setActive()
                    onPress.invoke()
                    handleController()
                }
            }
        }

        onUp {
            if (clicked) {
                pressing = false
                eventId = null
                clicked = false
                setIdle()
                onUp()
                handleController()
            }
        }

        onUpAnywhere {
            if (clicked) {
                pressing = false
                eventId = null
                clicked = false
                setIdle()
                onUpAnywhere()
                handleController()
            }
        }
    }

    private fun handleController() {
        controllerSignalData.pressing = pressing
        controllerSignal.invoke(controllerSignalData)
    }

    fun disable() {
        enabled = false
        if (disabledImageIcon != null) {
            disabledImageIcon.visible = true
            iconImage?.visible = false
        } else {
            iconImage?.visible = true
        }
        activeImageIcon?.visible = false
    }

    fun enable() {
        enabled = true
        if (forcedPressed) {
            setActive()
            return
        }
        disabledImageIcon?.visible = false
        iconImage?.visible = true
        activeImageIcon?.visible = false
    }

    fun setActive() {
        disabledImageIcon?.visible = false
        iconImage?.visible = false
        activeImageIcon?.visible = true
    }

    fun setIdle() {
        if (forcedPressed) {
            setActive()
            return
        }
        disabledImageIcon?.visible = false
        iconImage?.visible = true
        activeImageIcon?.visible = false
    }

    fun setScaleX(value: Double) {
        iconImage?.scaleX = value
        activeImageIcon?.scaleX = value
        disabledImageIcon?.scaleX = value
    }

    fun setScaleY(value: Double) {
        iconImage?.scaleY = value
        activeImageIcon?.scaleY = value
        disabledImageIcon?.scaleY = value
    }

    companion object {
        fun fitImageInRect(iconView: Image, bmp: BmpSlice, width: Double, height: Double, anchor: Anchor) {
            val iconScaleX = width / bmp.width
            val iconScaleY = height / bmp.height

            iconView.bitmap = bmp
            iconView.anchor(anchor)
            iconView.position(width * anchor.sx, height * anchor.sy)
            iconView.scale(iconScaleX, iconScaleY)

        }

        // todo
        fun fitTextSize(text: String): Double {
            if (text.length < 8) {
                return 48.0
            }

            if (text.length < 11) {
                return 40.0
            }

            return 36.0
        }
    }
}

fun Container.handleCustomButton(
    button: CustomButton,
    controller: ScreenController,
    onDown: () -> Unit = {},
    onUp: () -> Unit = {}
) {
    val view = this
    var pressing = false
    var eventId: Int? = null
    var clicked = false

    touch {
        start {
            if (button.enabled) {
                if (view.hitTest(it.global) != null &&
                    !pressing &&
                    eventId == null &&
                    !clicked
                ) {
                    pressing = true
                    eventId = it.id
                    onDown()
                    controller.onButton(button.buttonId, pressing)
                }
            }
        }

        end {
            if (it.id == eventId && !clicked) {
                eventId = null
                pressing = false
                onUp()
                controller.onButton(button.buttonId, pressing)
            }
        }
    }

    onDown {
        if (!pressing) {
            pressing = true
            clicked = true
            onDown()
            controller.onButton(button.buttonId, pressing)
        }
    }

    onUpAnywhere {
        if (clicked) {
            pressing = false
            eventId = null
            clicked = false
            onUp()
            controller.onButton(button.buttonId, pressing)
        }
    }
}
